"use client";

import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AuthService from "../../services/auth-service";
import { useAuth } from "../../contexts/AuthContext";

export const OAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState<string>("");
  const searchParams = useSearchParams()[0];
  const navigate = useNavigate();
  const { login, checkAuthStatus } = useAuth();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Get parameters from the URL
        const code = searchParams.get("code");
        const error = searchParams.get("error");
        const errorDescription = searchParams.get("error_description");
        const state = searchParams.get("state");
        
        // Check if there was an error
        if (error) {
          console.error('OAuth error:', error, errorDescription);
          setStatus("error");
          setMessage(`OAuth error: ${errorDescription || error}`);
          return;
        }
        
        // Check if we have a code
        if (!code) {
          console.error('No authorization code received');
          setStatus("error");
          setMessage("No authorization code received");
          return;
        }
        
        // Parse state parameter to check if this is a desktop app request
        let desktopRedirectUri: string | null = null;
        if (state) {
          try {
            const stateObj = JSON.parse(decodeURIComponent(state));
            desktopRedirectUri = stateObj.desktop_redirect || null;
          } catch (e) {
            console.warn('Failed to parse state parameter:', e);
          }
        }
        
        // Determine which provider we're dealing with based on the URL path
        const isGoogleCallback = window.location.pathname.includes('google');
        const isGitHubCallback = window.location.pathname.includes('github');
        
        // Handle the callback based on the provider
        let result;
        if (isGoogleCallback) {
          console.log('Processing Google OAuth callback with code:', code);
          result = await AuthService.handleGoogleCallback(code);
        } else if (isGitHubCallback) {
          // Handle GitHub callback
          console.log('Processing GitHub OAuth callback with code:', code);
          result = await AuthService.handleGitHubCallback(code);
        } else {
          // Default to GitHub for other callbacks if it looks like a GitHub callback
          console.log('Processing GitHub OAuth callback with code:', code);
          result = await AuthService.handleGitHubCallback(code);
        }
        
        if (result.success) {
          // Update the auth context state
          // Force a check of the auth status to ensure it's up to date
          checkAuthStatus();
          
          setStatus("success");
          
          // If this is a desktop app request, redirect to the desktop app
          if (desktopRedirectUri) {
            setMessage("Authentication successful! Redirecting to desktop app...");
            console.log("Redirecting to desktop app with token:", result.token);
            // Redirect to desktop app with token
            window.location.href = `${desktopRedirectUri}?token=${result.token}`;
            return;
          }
          
          // Redirect to main profile page
          setMessage("Authentication successful! Redirecting to your profile...");
          console.log("OAuth successful, redirecting to profile");
          setTimeout(() => {
            navigate("/profile");
          }, 2000);
        } else {
          console.error('OAuth failed:', result.error);
          setStatus("error");
          setMessage(result.error || "Authentication failed");
        }
      } catch (err) {
        console.error("OAuth callback error:", err);
        setStatus("error");
        setMessage("An error occurred during authentication");
      }
    };
    
    handleOAuthCallback();
  }, [searchParams, navigate, login, checkAuthStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8">
        {status === "loading" && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-foreground">Processing authentication...</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="rounded-full bg-green-100 p-3 w-16 h-16 flex items-center justify-center mx-auto">
              <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="mt-4 text-foreground font-medium">{message}</p>
          </>
        )}
        
        {status === "error" && (
          <>
            <div className="rounded-full bg-red-100 p-3 w-16 h-16 flex items-center justify-center mx-auto">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="mt-4 text-foreground font-medium">{message}</p>
            <button 
              className="mt-6 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              onClick={() => navigate("/")}
            >
              Return to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;